import { useCallback } from "react"

import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "./useTransportQuery"

const GET_LINEAR_STATUS = "getLinearIntegrationStatus" as const
const GET_LINEAR_AUTHORIZATION_URL = "getLinearAuthorizationUrl" as const
const CONNECT_LINEAR = "connectLinearIntegration" as const
const DISCONNECT_LINEAR = "disconnectLinearIntegration" as const
const LIST_LINEAR_TEAMS = "listLinearTeams" as const
const LIST_LINEAR_PROJECTS = "listLinearProjects" as const
const CREATE_LINEAR_ISSUE = "createLinearIssue" as const

export function useLinearStatusTransport() {
  const transportQueryUtils = useTransportQueryUtils()
  const statusQuery = useTransportQuery<typeof GET_LINEAR_STATUS>({
    operation: GET_LINEAR_STATUS,
  })
  const authorizationUrlQuery = useTransportQuery<
    typeof GET_LINEAR_AUTHORIZATION_URL
  >({
    operation: GET_LINEAR_AUTHORIZATION_URL,
    options: {
      enabled: false,
    },
  })

  const refreshStatus = useCallback(
    () => transportQueryUtils.invalidate(GET_LINEAR_STATUS),
    [transportQueryUtils],
  )

  const getAuthorizationUrl = useCallback(async () => {
    const result = await authorizationUrlQuery.refetch()
    if (!result.data?.authorization_url) {
      throw new Error("Unable to start Linear OAuth.")
    }
    return result.data.authorization_url
  }, [authorizationUrlQuery])

  return {
    isLoading:
      statusQuery.isLoading || (!statusQuery.data && !statusQuery.error),
    isStartingOAuth: authorizationUrlQuery.isFetching,
    refreshStatus,
    getAuthorizationUrl,
    status: statusQuery.data,
  }
}

export function useLinearConnectionTransport() {
  const transportQueryUtils = useTransportQueryUtils()
  const connectMutation = useTransportMutation<typeof CONNECT_LINEAR>(
    CONNECT_LINEAR,
    {
      onSuccess: () => {
        void transportQueryUtils.invalidate(GET_LINEAR_STATUS)
      },
    },
  )
  const disconnectMutation = useTransportMutation<typeof DISCONNECT_LINEAR>(
    DISCONNECT_LINEAR,
    {
      onSuccess: () => {
        void transportQueryUtils.invalidate(GET_LINEAR_STATUS)
      },
    },
  )

  const connect = useCallback(
    async (code: string, state: string) => {
      await connectMutation.mutateAsync({ code, state })
    },
    [connectMutation],
  )

  const disconnect = useCallback(async () => {
    await disconnectMutation.mutateAsync(undefined)
  }, [disconnectMutation])

  return {
    connect,
    disconnect,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  }
}

export function useLinearTeamsTransport(isOpen: boolean) {
  const teamsQuery = useTransportQuery<typeof LIST_LINEAR_TEAMS>({
    operation: LIST_LINEAR_TEAMS,
    options: {
      enabled: isOpen,
    },
  })

  return {
    isLoading: teamsQuery.isLoading,
    teamsError: teamsQuery.error,
    teams: teamsQuery.data?.teams ?? [],
  }
}

export function useLinearProjectsTransport(isOpen: boolean, teamId: string) {
  const projectsQuery = useTransportQuery<typeof LIST_LINEAR_PROJECTS>({
    operation: LIST_LINEAR_PROJECTS,
    request: { team_id: teamId },
    options: {
      enabled: isOpen && teamId.length > 0,
    },
  })

  return {
    projectsError: projectsQuery.error,
    projects: projectsQuery.data?.projects ?? [],
  }
}

export function useLinearIssueTransport() {
  const createIssueMutation =
    useTransportMutation<typeof CREATE_LINEAR_ISSUE>(CREATE_LINEAR_ISSUE)

  const createIssue = useCallback(
    async (input: {
      description?: string
      priority?: number
      projectId?: string
      teamId: string
      title: string
    }) => {
      const response = await createIssueMutation.mutateAsync({
        description: input.description,
        priority: input.priority,
        project_id: input.projectId,
        team_id: input.teamId,
        title: input.title,
      })
      return response.issue
    },
    [createIssueMutation],
  )

  return {
    createIssue,
    isCreating: createIssueMutation.isPending,
  }
}
